import React from 'react';
import Layout from './Layout';
import { motion } from 'framer-motion';
import { FaFileExcel, FaChartBar, FaDownload } from 'react-icons/fa';

const AArticleTheDashboard = () => {
  const sections = [
    {
      id: 1,
      title: "Introduction to Dashboards",
      text: `Imagine that you're driving a car. You need to know how fast you're going, how far you've traveled, and whether you need to stop for a break. Not keeping track of these three things might get you in trouble. Speeding can result in a ticket, not having enough gas can result in you getting stranded, and without sufficient upkeep of the car's high mileage might result in a breakdown. In a car, there's a small dial that shows this information called a speedometer. The dial, or spedometer, is analogous to a small dashboard for your car.

In business, a dashboard works the same way. It shows you important information about your business so you can make smart choices. Instead of showing your speed or distance, it highlights things like how much money you're making, how many customers you have, or whether your products are selling well.

A dashboard takes a lot of complicated data and turns it into something simple and easy to understand. It's like a control panel for your business, helping you see what's working and what needs fixing. Without a dashboard, you'd be guessing whether things are going well or not. But with it, you get a clear picture of your business performance.`,
      explanation: "Understanding what dashboards are through a practical, everyday analogy"
    },
    {
      id: 2,
      title: "Why Track Progress?",
      text: `Now imagine you're planting a garden. You can't just plant seeds and forget about them. You need to keep an eye on them to see if they're growing. You check if they have enough water, if they're getting enough sunlight, and if weeds are taking over. This helps you know if your plants are healthy or if you need to do something different.

Tracking progress in a business is like watching over a garden. You look at key pieces of information, called metrics, to see how things are going. Below is a four step explaination of why tracking progress is important:

Spot Problems Early: Just like noticing weeds in your garden before they spread, dashboards help you see problems in your business before they get worse. For example, if sales are dropping, a dashboard will show it so you can fix it quickly. Further, the dashboard will highlight the source of the sales drop as the data is right there!

Enables Smart Choices: Instead of guessing what to do next, you use the data from your dashboard to make informed decisions. For example, if you see that a product isn't selling well, you can decide to improve it, promote it more or maybe even discontinue the product.

Set and Reach Goals: Back to the garden analogy, if you want to grow a big sunflower, you need to know how tall it is now and how much it grows each day. In business, dashboards help you set goals and track your progress, resulting in a comprehensive timeline showing when goal completion can be expected.

Know What Works: When you try different ways to help your plants grow, like watering more or moving them to a sunnier spot, you see what changes makes a difference in the plant's growth. In business, dashboards show you what actions help your business succeed. For example, you might have an amazing salesperson in your business that outperforms their peers and your dashboard highlights this particularly productive salesperson, by this, the dashboard lets you know that you should keep this employee. Another example might be that you have a product that is adored in a region with high margins, your dashboard will highlight that you should consider increasing the production of that product and ship it to the region where it is in high demand.

In summation, dashboards tell your business's story through data. Without them, you might miss important signs of success or trouble. With them, you can make sure your business stays on track and grows strong, just like a well-tended garden.`,
      explanation: "The importance of data-driven decision making"
    },
    {
      id: 3,
      title: "Building Your First Dashboard",
      text: `I have prepared two resources to help you get started:

1. An Excel template with sample data
2. A PowerBI dashboard that visualizes the sample data from the excel sheet

Download these files below to practice creating your own dashboard. Further I suggest you play around with the different filters and try to come up with stories for why the numbers present as they do and what initiatives can be taken to improve the numbers.`,
      downloads: [
        {
          name: "Sample Data.xlsx",
          icon: <FaFileExcel className="text-green-600" />,
          path: "/files/Sample Data.xlsx"
        },
        {
          name: "Dashboard Example.pbix",
          icon: <FaChartBar className="text-blue-600" />,
          path: "/files/Dashboard Example.pbix"
        }
      ],
      explanation: "Practical resources for dashboard creation. Further note that the data in these files is fictional and for educational purposes only."
    }
  ];

  const handleDownload = (path, filename) => {
    const fullPath = process.env.PUBLIC_URL + path;
    fetch(fullPath)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div>
      <Layout />
      <div className="w-full min-h-screen bg-gradient-to-b from-white to-white text-black pt-20">
        <div className="max-w-screen-lg mx-auto p-4">
          {/* Add Table of Contents */}
          <div className="mb-12 flex justify-center">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm max-w-[600px] w-full">
              <h2 className="text-2xl font-bold mb-4 text-center">Table of Contents</h2>
              <div className="space-y-2">
                {sections.map(section => section.title && (
                  <div 
                    key={section.id} 
                    className="cursor-pointer hover:scale-110 transition-colors text-center"
                    onClick={() => scrollToSection(`section-${section.id}`)}
                  >
                    {section.id}. {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-16">
            {sections.map(({ id, title, text, downloads, explanation }, index) => (
              <motion.div 
                key={id}
                id={`section-${id}`}  // Add this line for scroll targeting
                initial={{ opacity: 1, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: index * 0.3 }}
                className="flex flex-col gap-6"
              >
                {/* Rest of your existing section content */}
                <h2 className="text-3xl font-bold">{title}</h2>
                <div className="text-lg whitespace-pre-wrap">{text}</div>
                {downloads && (
                  <div className="flex flex-col sm:flex-row gap-4">
                    {downloads.map((download, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleDownload(download.path, download.name)}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {download.icon}
                        <span>{download.name}</span>
                        <FaDownload className="ml-2" />
                      </button>
                    ))}
                  </div>
                )}
                {explanation && (
                  <p className="text-gray-600 italic">{explanation}</p>
                )}
              </motion.div>
            ))}
          </div>

          {/* Add Back to Top Button */}
          <div className="flex justify-center mt-12 mb-8">
            <button
              onClick={scrollToTop}
              className="text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors duration-300 hover:scale-110"
              style={{ background: 'rgb(1, 50, 32)' }}
            >
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AArticleTheDashboard;